import * as React from 'react'

import {
  AtSignIcon,
  CircleCheckIcon,
  EyeIcon,
  FileTextIcon,
  FlagIcon,
  FolderIcon,
  LucideIcon,
  PlayIcon,
  RefreshCwIcon,
} from 'lucide-react'

import {
  getPictogramGlyph,
  LocationTypeIcon,
  LOCATION_ICON_COMPONENTS,
  StickerIcon,
  STICKER_ICON_DATA,
} from '~/client/src/shared/components/SitemapHelpers/utils/markerSpec'
import * as TagIcon from '~/client/src/shared/components/TagIcon'
import { TagIconType } from '~/client/src/shared/enums/TagIcon'
import { WorkflowIcon } from '~/client/src/shared/icons'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import SitemapItem from '~/client/src/shared/models/SitemapItem'

// The app's standard Lucide box, for a caller that passes no size rather than falling through
// to Lucide's own 24. Its 3..21 inset puts 12px of ink in the box, matching what TagIcon's
// 12px edge-to-edge default drew — so `size` stays the box, as every other Lucide caller
// expects, and rows sized off the icon keep their height.
const TAG_ICON_SIZE = 16

interface IProps {
  dataObject?: LocationBase
  sitemapItem?: SitemapItem
  customProps?: any
  shouldUseAlternativeIcon?: boolean
}

export default class SitemapAttributeIconComponent extends React.Component<IProps> {
  public static defaultProps = {
    customProps: {},
  }

  public render() {
    const { dataObject, sitemapItem, customProps, shouldUseAlternativeIcon } =
      this.props

    if (dataObject) {
      const { color } = dataObject

      if (
        dataObject.iconName === TagIconType.Project &&
        shouldUseAlternativeIcon
      ) {
        return this.renderLucideIcon(FlagIcon, color)
      }

      // Location types and pictograms resolve from the tables the map and the setup pallet
      // read, so one icon cannot show different artwork per surface. Strict lookup: statuses
      // and people are neither and fall through to the cases below.
      const LocationIcon =
        LOCATION_ICON_COMPONENTS[dataObject.iconName as LocationTypeIcon] ??
        getPictogramGlyph(dataObject.iconName)?.Icon
      if (LocationIcon) {
        return this.renderLucideIcon(LocationIcon, color)
      }

      switch (dataObject.iconName as string) {
        case TagIconType.Company:
          return <TagIcon.Company color={color} {...customProps} />

        case TagIconType.ToInspect:
        case TagIconType.Status:
        case TagIconType.Done:
          return <TagIcon.StatusDone color={color} {...customProps} />

        case TagIconType.Requested:
        case TagIconType.Submitted:
          return <TagIcon.StatusRequested color={color} {...customProps} />

        case TagIconType.Scheduled:
        case TagIconType.Canceled:
        case TagIconType.Paused:
        case TagIconType.OnHold:
          return <TagIcon.StatusScheduled color={color} {...customProps} />

        case TagIconType.OnSite:
        case TagIconType.Delivering:
          return <TagIcon.StatusOnSite color={color} {...customProps} />

        case TagIconType.Accepted:
        case TagIconType.PassedInspection:
        case TagIconType.Passed:
          return <TagIcon.StatusPassed color={color} {...customProps} />

        case TagIconType.FailedInspection:
        case TagIconType.Denied:
        case TagIconType.Failed:
          return <TagIcon.StatusFailed color={color} {...customProps} />

        case TagIconType.Role:
          return <TagIcon.Role color={color} {...customProps} />

        case TagIconType.Team:
          return <TagIcon.Team color={color} {...customProps} />

        case TagIconType.DefaultTeam:
          return <TagIcon.DefaultTeam color={color} {...customProps} />

        case TagIconType.Trade:
          return <TagIcon.Trade color={color} {...customProps} />

        case TagIconType.User:
          return <TagIcon.User color={color} {...customProps} />

        case TagIconType.At:
          return this.renderLucideIcon(AtSignIcon, color)

        case TagIconType.Changed:
          return this.renderLucideIcon(RefreshCwIcon, color)

        case TagIconType.IncompleteDone:
        case TagIconType.Ended:
          return this.renderLucideIcon(CircleCheckIcon, color)

        case TagIconType.Reviewed:
          return this.renderLucideIcon(EyeIcon, color)

        case TagIconType.Active:
          return this.renderLucideIcon(PlayIcon, color)

        case TagIconType.FormCategory:
          return this.renderLucideIcon(FolderIcon, color)

        case TagIconType.FormType:
          return this.renderLucideIcon(WorkflowIcon, color)

        case TagIconType.Draft:
          return this.renderLucideIcon(FileTextIcon, color)
      }
    }

    // A sticker's artwork comes from the same table its pallet button and placement cursor read.
    const stickerGlyph = STICKER_ICON_DATA[sitemapItem?.iconName as StickerIcon]
    if (stickerGlyph) {
      return this.renderLucideIcon(stickerGlyph.Icon, sitemapItem.color)
    }

    return null
  }

  // Colour rides CSS because Lucide's color prop sets only stroke, and some glyphs are
  // filled — currentColor drives both. `display` because Lucide leaves its svg inline, which
  // would sit on the text baseline and leave descender space below it; callers size their row
  // and the tag background off the icon box, so it has to be exactly `size` tall.
  private renderLucideIcon(Icon: LucideIcon, color: string): JSX.Element {
    const { size = TAG_ICON_SIZE, ...glyphProps } = this.props.customProps as {
      size?: number
    }

    return (
      <Icon size={size} style={{ color, display: 'block' }} {...glyphProps} />
    )
  }
}
